<template>
  <div>
    <refund-modal
      @close="closeBillPaymentRefundModal"
      v-if="showBillPaymentsRefundModal"
      :bill-payment="billPaymentToRefund"
    />
    <bill-payments-list-modal
      @close="closeBillPaymentsModal"
      @refundBillPayment="refundBillPayment"
      v-if="showBillPaymentsModal"
    />
    <resend-boleto-modal
      @close="closeResendBoletoModal"
      v-if="showResendBoletoModal"
    />
    <ignore-bill-modal
      @close="closeIgnoreBillModal"
      v-if="showIgnoreBillModal"
    />
    <change-bill-due-date-modal
      @close="closeBillDueDateModal"
      v-if="showBillDueDateModal"
    />
    <change-bill-value-modal
      @close="closeBillValueModal"
      v-if="showBillValueModal"
    />
    <discount-charges-modal
      @close="closeDiscountChargesModal"
      v-if="showDiscountChargesModal"
    />
    <generate-boleto-modal
      @close="closeGenerateBoletoModal"
      :student-email="student.email"
      v-if="showGenerateBoletoModal"
    />
    <bill-detail-modal
      @close="closeBillDetailModal"
      @open-discount-modal="openDiscountModal"
      @open-make-bill-paid-modal="openMakeBillPaidModal"
      v-if="showBillDetailModal"
    />
    <bill-dilution-modal
      @close="closeBillDilutionModal"
      v-if="showBillDilutionModal"
    />
    <edit-external-id-modal
      @close="closeEditExternalIdModal"
      v-if="showEditExternalIdModal"
    />
    <discount-modal
      @close="closeDiscountModal"
      v-if="showDiscountModal"
    />
    <make-bill-paid-modal
      @close="closeMakeBillPaidModal"
      v-if="showMakeBillPaidModal"
    />
    <bill-reactivation-modal
      @close="closeBillReactivationModal"
      v-if="showBillReactivationModal"
    />
    <loading v-if="!bills" class="cards__loading" full-screen="false" size="small" />
    <table v-else class="table table-striped panel-table">
      <thead>
        <tr>
          <th class="table-centralized">
            ID
          </th>
          <th class="table-centralized">
            Tipo
          </th>
          <th class="table-centralized">
            Valor
          </th>
          <th class="table-centralized">
            Valor pago
          </th>
          <th class="table-centralized">
            Vencimento
          </th>
          <th class="table-centralized">
            Pagamento
          </th>
          <th class="table-centralized">
            Status
          </th>
          <th class="table-centralized">
            Pode ter juros
          </th>
          <th class="table-centralized" colspan="2">
            <dropdown-filter-list
              text-displayed="Filtro de mensalidades"
              :reference-id="`bills-${contractId}`"
              :items-filter="billsFilter"
              :pre-selected-items="billStatusFilter"
              @items-changed="billsFilterChanged"
            />
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="bills.length === 0">
          <td colspan="9" class="table-centralized">
            Não existem mensalidades para este contrato, ou para os filtros aplicados
          </td>
        </tr>
        <tr v-else v-for="bill in bills" :key="bill.id" class="table-centralized">
          <td>{{ bill.id }}</td>
          <td>
            {{ bill.translated_bill_type }}
            <z-text size="small" tag="span" v-if="reenrollmentBill(bill)">
              (Rematrícula)
            </z-text>
          </td>
          <td>{{ bill.amount_with_charges | formatCurrency }}</td>
          <td>{{ bill.paid_amount | formatCurrency }}</td>
          <td>{{ bill.due_date | formatDate }}</td>
          <td>{{ bill.paid_date | formatDate }}</td>
          <td>{{ bill.translated_status }}</td>
          <td>{{ bill.apply_charges | formatBoolean }}</td>
          <td>
            <div>
              <button
                title="Ver detalhes da mensalidade"
                class="btn btn-primary bill-actions-button"
                @click="openBillDetailModal(bill)"
              >
                <i class="fas fa-eye"></i>
              </button>

              <button
                :title="isBillIgnored(bill) ? 'Essa mensalidade está ignorada' : 'Ver pagamentos'"
                class="btn btn-primary bill-actions-button"
                @click="openBillPaymentsModal(bill)"
                :disabled="isBillIgnored(bill)"
              >
                <i class="fas fa-list"></i>
              </button>

              <button
                :title="boletoBarCodeTitle(bill)"
                class="btn btn-primary bill-actions-button"
                @click="copyCode(bill.current_bill_payment.bar_code)"
                :disabled="!bill.current_bill_payment || isBillIgnored(bill)"
              >
                <i class="fas fa-barcode"></i>
              </button>

              <button
                :title="pixCodeTitle(bill)"
                class="btn btn-primary bill-actions-button"
                @click="copyCode(bill.current_pix_pending.metadata.qr_code)"
                :disabled="!bill.current_pix_pending || isBillIgnored(bill)"
              >
                <i class="fas fa-qrcode"></i>
              </button>
            </div>
          </td>

          <td>
            <div class="dropdown">
              <button
                class="btn btn-primary dropdown-toggle"
                :id="`dropdown-bill-options-${bill.id}`"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="true"
                :disabled="!canShowActionsButton(bill)"
                :title="!canShowActionsButton(bill) ? 'Essa mensalidade não pode ser alterada' : ''"
              >
                Ações
                <span class="caret"></span>
              </button>
              <ul class="dropdown-menu" :aria-labelledby="`dropdown-bill-options-${bill.id}`">
                <template v-if="canEditBill(bill)">
                  <li>
                    <a @click="openResendBoletoModal(bill)">
                      Reenviar pix e boleto
                    </a>
                  </li>
                  <li>
                    <a @click="openBillDueDateModal(bill)">
                      Alterar vencimento
                    </a>
                  </li>
                  <li>
                    <a @click="openIgnoreBillModal(bill)">
                      Ignorar mensalidade
                    </a>
                  </li>
                  <li>
                    <a @click="openBillDilutionModal(bill)">
                      Diluir mensalidade
                    </a>
                  </li>
                  <li>
                    <a @click="openEditExternalIdModal(bill)">
                      Editar ID externo
                    </a>
                  </li>
                </template>
                <template>
                  <li
                    v-if="bill.can_charge"
                    v-permissions:bill.update
                  >
                    <a @click="openBillValueModal(bill)">
                      Alterar valor
                    </a>
                  </li>
                  <li
                    v-if="bill.can_charge && !bill.has_payments"
                    v-permissions:bill.toggle_charges
                  >
                    <a @click="openDiscountChargesModal(bill)">
                      {{ bill.apply_charges ? 'Remover encargos' : 'Reabilitar encargos' }}
                    </a>
                  </li>
                  <li v-if="bill.status === 'pending'">
                    <a @click="openGenerateBoletoModal(bill)">
                      Gerar boleto para nova data
                    </a>
                  </li>
                  <li v-if="isBillIgnoredOrCanceled(bill)">
                    <a @click="openBillReactivationModal(bill)">
                      Reativar mensalidade
                    </a>
                  </li>
                </template>
              </ul>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import 'moment/locale/pt-br';
import Permissions from 'mixins/Permissions';
import Loading from 'components/Loading';
import DropdownFilterList from 'components/DropdownFilterList';
import Clipboard from 'utils/Clipboard';
import BillPaymentsListModal from './modals/BillPaymentsListModal';
import ResendBoletoModal from './modals/ResendBoletoModal';
import IgnoreBillModal from './modals/IgnoreBillModal';
import BillReactivationModal from './modals/BillReactivationModal';
import ChangeBillDueDateModal from './modals/ChangeBillDueDateModal';
import ChangeBillValueModal from './modals/ChangeBillValueModal';
import DiscountChargesModal from './modals/DiscountChargesModal';
import GenerateBoletoModal from './modals/GenerateBoletoModal';
import BillDetailModal from './modals/BillDetailModal';
import BillDilutionModal from './modals/BillDilutionModal';
import EditExternalIdModal from './modals/EditExternalIdModal';
import DiscountModal from './modals/DiscountModal';
import MakeBillPaidModal from './modals/MakeBillPaidModal';
import RefundModal from './modals/RefundModal';

export default {
  mixins: [Permissions],
  props: {
    contractId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      billsFilter: [
        { value: 'pending', text: 'Atrasadas' },
        { value: 'canceled', text: 'Canceladas' },
        { value: 'open', text: 'Em aberto' },
        { value: 'ignored', text: 'Ignoradas' },
        { value: 'paid', text: 'Pagas' },
      ],
      billStatusFilter: [
        'open',
        'pending',
        'paid',
      ],
      showBillPaymentsModal: false,
      showResendBoletoModal: false,
      showIgnoreBillModal: false,
      showBillDueDateModal: false,
      showBillValueModal: false,
      showDiscountChargesModal: false,
      showGenerateBoletoModal: false,
      showBillDetailModal: false,
      showBillDilutionModal: false,
      showEditExternalIdModal: false,
      showDiscountModal: false,
      showMakeBillPaidModal: false,
      showBillPaymentsRefundModal: false,
      showBillReactivationModal: false,
      billPaymentToRefund: null,
    };
  },
  components: {
    Loading,
    BillPaymentsListModal,
    ResendBoletoModal,
    IgnoreBillModal,
    ChangeBillDueDateModal,
    ChangeBillValueModal,
    DiscountChargesModal,
    GenerateBoletoModal,
    DropdownFilterList,
    BillDetailModal,
    BillDilutionModal,
    EditExternalIdModal,
    DiscountModal,
    MakeBillPaidModal,
    RefundModal,
    BillReactivationModal,
  },
  computed: {
    ...mapState([
      'student',
      'studentBills',
      'selectedBill',
    ]),
    bills() {
      return (this.studentBills && this.contractId) ? this.getBills() : null;
    },
  },
  methods: {
    ...mapActions([
      'loadSelectedBill',
      'loadStudentBills',
      'loadPaymentDetails',
    ]),
    ...mapMutations([
      'setSelectedDiscount',
      'setSelectedBill',
      'setPaymentDetails',
    ]),
    getBills() {
      return this.studentBills
        .filter(bill => bill.contract_id === this.contractId && this.billStatusFilter.includes(bill.status));
    },
    copyCode(code) {
      Clipboard.copy(code);
      this.$alert.notifySuccess('Código copiado com sucesso!');
    },
    openGenerateBoletoModal(bill) {
      this.setSelectedBill(bill);
      this.showGenerateBoletoModal = true;
    },
    closeGenerateBoletoModal() {
      this.showGenerateBoletoModal = false;
    },
    billsFilterChanged(items) {
      this.billStatusFilter = items;
    },
    openBillPaymentsModal(bill) {
      this.setSelectedBill(bill);
      this.showBillPaymentsModal = true;
    },
    closeBillPaymentsModal() {
      this.showBillPaymentsModal = false;
      this.setSelectedBill(null);
    },
    closeIgnoreBillModal() {
      this.showIgnoreBillModal = false;
      this.setSelectedBill(null);
    },
    openIgnoreBillModal(bill) {
      this.setSelectedBill(bill);
      this.showIgnoreBillModal = true;
    },
    closeResendBoletoModal() {
      this.showResendBoletoModal = false;
      this.setSelectedBill(null);
    },
    openResendBoletoModal(bill) {
      this.setSelectedBill(bill);
      this.showResendBoletoModal = true;
    },
    openBillDueDateModal(bill) {
      this.setSelectedBill(bill);
      this.showBillDueDateModal = true;
    },
    closeBillDueDateModal() {
      this.showBillDueDateModal = false;
      this.setSelectedBill(null);
    },
    openBillValueModal(bill) {
      this.setSelectedBill(bill);
      this.showBillValueModal = true;
    },
    closeBillValueModal() {
      this.showBillValueModal = false;
      this.setSelectedBill(null);
    },
    openDiscountChargesModal(bill) {
      this.setSelectedBill(bill);
      this.showDiscountChargesModal = true;
    },
    closeDiscountChargesModal() {
      this.showDiscountChargesModal = false;
      this.setSelectedBill(null);
    },
    refundBillPayment(billPayment) {
      if (['paid', 'partial'].includes(billPayment.status)) {
        this.billPaymentToRefund = billPayment;
        this.showBillPaymentsModal = false;
        this.setSelectedBill(null);
        this.showBillPaymentsRefundModal = true;
      }
    },
    closeBillPaymentRefundModal() {
      this.billPaymentToRefund = null;
      this.showBillPaymentsRefundModal = false;
    },
    openBillDetailModal(bill) {
      this.loadSelectedBill(bill.id).then(() => {
        this.showBillDetailModal = true;
      });
    },
    closeBillDetailModal() {
      this.loadStudentBills().then(() => {
        this.showBillDetailModal = false;
        this.setSelectedBill(null);
      });
    },
    openBillDilutionModal(bill) {
      this.setSelectedBill(bill);
      this.showBillDilutionModal = true;
    },
    closeBillDilutionModal() {
      this.showBillDilutionModal = false;
      this.setSelectedBill(null);
    },
    openEditExternalIdModal(bill) {
      this.setSelectedBill(bill);
      this.showEditExternalIdModal = true;
    },
    closeEditExternalIdModal() {
      this.showEditExternalIdModal = false;
      this.setSelectedBill(null);
    },
    openDiscountModal() {
      this.showBillDetailModal = false;
      this.showDiscountModal = true;
    },
    closeDiscountModal() {
      this.loadSelectedBill(this.selectedBill.id).then(() => {
        this.setSelectedDiscount(null);
        this.showBillDetailModal = true;
        this.showDiscountModal = false;
      });
    },
    openMakeBillPaidModal() {
      this.loadPaymentDetails(this.selectedBill.id).then(() => {
        this.showMakeBillPaidModal = true;
        this.showBillDetailModal = false;
      });
    },
    closeMakeBillPaidModal() {
      this.loadSelectedBill(this.selectedBill.id).then(() => {
        this.setPaymentDetails(null);
        this.showMakeBillPaidModal = false;
        this.showBillDetailModal = true;
      });
    },
    openBillReactivationModal(bill) {
      this.setSelectedBill(bill);
      this.showBillReactivationModal = true;
    },
    closeBillReactivationModal() {
      this.showBillReactivationModal = false;
      this.setSelectedBill(null);
    },
    canEditBill(bill) {
      return ['open', 'pending'].includes(bill.status) && bill.bill_type !== 'admission';
    },
    canReactivateBill(bill) {
      return this.isBillIgnoredOrCanceled(bill)
        && bill.bill_type === 'installment'
        && bill.billing_status === 'active'
        && this.userPermissions.bill.includes('reactivate');
    },
    isBillIgnoredOrCanceled(bill) {
      return ['canceled', 'ignored'].includes(bill.status);
    },
    isBillIgnored(bill) {
      return bill.status === 'ignored';
    },
    canShowActionsButton(bill) {
      return this.canEditBill(bill) || this.canReactivateBill(bill);
    },
    boletoBarCodeTitle(bill) {
      if (this.isBillIgnored(bill)) {
        return 'Essa mensalidade está ignorada';
      } if (!bill.current_bill_payment) {
        return 'Essa mensalidade não possui boleto ativo';
      }

      return 'Copiar código do boleto';
    },
    pixCodeTitle(bill) {
      if (this.isBillIgnored(bill)) {
        return 'Essa mensalidade está ignorada';
      } if (!bill.current_pix_pending) {
        return 'Essa mensalidade não possui pix ativo';
      }

      return 'Copiar código pix';
    },
    reenrollmentBill(bill) {
      return bill.metadata && bill.metadata.reenrollment_id;
    },
  },
};
</script>

<style lang="scss" scoped>
  .bill-actions-button:not(:last-child) {
    margin-right: 4px;
  }
</style>
